import { useCallback, useMemo, useRef, useState } from 'react';
import debounce from 'react-utils/debounce';
import { useHttpClient } from '../../client/HttpClientContext';
import { Metrics } from '../../metrics/Metrics';
import { useItemRegistry } from './useItemRegistry';
import { useAutosaveWindowEvents } from './useAutosaveWindowEvents';
import { useOnObjectsUpdated } from './useOnObjectsUpdated';
import { useAutosaveStatus } from './useAutosaveStatus';
import { savePropertyItems, undoPropertyItems } from '../property';
export const useAutosaveCore = () => {
  const httpClient = useHttpClient();
  const onObjectsUpdated = useOnObjectsUpdated();
  const {
    status,
    statusHandlers
  } = useAutosaveStatus();
  const handlersRef = useRef(new Map());
  const [lastSavedItems, setLastSavedItems] = useState([]);
  const itemRegistry = useItemRegistry();
  const inFlightSaveCount = useRef(0);
  const getHasInFlightSaves = useCallback(() => inFlightSaveCount.current > 0, [inFlightSaveCount]);
  useAutosaveWindowEvents(getHasInFlightSaves);
  const undo = useCallback(() => {
    statusHandlers.undoStarted();
    void undoPropertyItems(lastSavedItems, httpClient, onObjectsUpdated);
  }, [statusHandlers, lastSavedItems, httpClient, onObjectsUpdated]);
  const save = useCallback(items => {
    inFlightSaveCount.current++;
    statusHandlers.savingStarted();
    savePropertyItems(items, httpClient, onObjectsUpdated).then(allSuccess => {
      if (allSuccess) {
        statusHandlers.savingSucceeded();
      } else {
        statusHandlers.savingFailed();
      }
    }).catch(() => {}).finally(() => {
      setLastSavedItems(items);
      inFlightSaveCount.current--;
    });
  }, [statusHandlers, httpClient, onObjectsUpdated]);
  const saveValidRequests = useCallback(() => {
    const handlers = Array.from(handlersRef.current.values());
    const itemsToProcess = itemRegistry.getAll().filter(item => handlers.every(({
      getIsHandlerForRequest,
      getIsValidRequest
    }) => !getIsHandlerForRequest(item.data.request) || getIsValidRequest(item.data.request)));
    if (itemsToProcess.length > 0) {
      itemRegistry.filter(item => !itemsToProcess.includes(item));
      save(itemsToProcess);
    }
  }, [save, itemRegistry]);
  const cancelRequestsForForm = useCallback(formId => {
    for (const item of itemRegistry.getAll()) {
      var _item$data$request$fo;
      if (((_item$data$request$fo = item.data.request.formMetadata) === null || _item$data$request$fo === void 0 ? void 0 : _item$data$request$fo.formId) === formId) {
        itemRegistry.remove(item.id);
        item.data.onClear();
      }
    }
  }, [itemRegistry]);
  const cancelRequestsForSource = useCallback(sourceId => {
    if (itemRegistry.hasItem(sourceId)) {
      itemRegistry.remove(sourceId);
    }
  }, [itemRegistry]);
  const clearPendingRequests = useCallback(() => {
    const items = itemRegistry.getAll();
    itemRegistry.clear();
    for (const item of items) {
      item.data.onClear();
    }
    statusHandlers.requestsCleared();
    Metrics.counter('autosave-clear-pending-requests').increment();
  }, [itemRegistry, statusHandlers]);
  const enqueueRequest = useCallback(operation => {
    itemRegistry.add({
      id: operation.request.sourceId,
      data: operation,
      timestamp: Date.now()
    });
  }, [itemRegistry]);
  const registerHandler = useCallback(config => {
    handlersRef.current.set(config.id, config);
    return () => {
      handlersRef.current.delete(config.id);
    };
  }, []);
  const getRegisteredFieldCount = useCallback(() => handlersRef.current.size, []);
  const items = itemRegistry.getAll();
  const requests = useMemo(() => items.map(item => item.data.request), [items]);
  const invalidRequests = useMemo(() => {
    // eslint-disable-next-line react-hooks/refs
    const handlers = Array.from(handlersRef.current.values());
    return items.filter(item => handlers.some(({
      getIsHandlerForRequest,
      getIsValidRequest
    }) => getIsHandlerForRequest(item.data.request) && !getIsValidRequest(item.data.request))).map(item => item.data.request);
  }, [items]);
  return {
    cancelRequestsForSource,
    cancelRequestsForForm,
    clearPendingRequests,
    enqueueRequest,
    getRegisteredFieldCount,
    hasRequestForSource: itemRegistry.hasItem,
    invalidRequests,
    lastSavedItems,
    registerHandler,
    requests,
    saveValidRequests: useMemo(
    // eslint-disable-next-line react-hooks/refs
    () => debounce(saveValidRequests, 25), [saveValidRequests]),
    saveValidRequestsImmediate: saveValidRequests,
    status,
    undo
  };
};